import { Trans, useLingui } from "@lingui/react/macro";
import { m } from "framer-motion";
import { Check, Clock, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";

import type { CheckoutState } from "./checkout-types";

// Legacy source: src/components/billing/checkout/checkout-step-success.tsx (1:1 port).
// Differences vs legacy:
// - next-intl t("onboarding*") → Lingui <Trans>/t with the EN values as msgids.
// - Legacy imported next/navigation useRouter but never used it — dropped.
// - SPA routes are NOT locale/country-prefixed, so navigateAndClose uses the
//   plain path; `locale` + `country` stay in the prop contract for parity with
//   legacy callers (checkout-modal) but are unused here.
// - Legacy onboardingUserFeature4 is an empty string in both en.json and
//   ro.json — an empty msgid is invalid in Lingui, so that bullet is omitted.

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CheckoutStepSuccessProps {
  state: CheckoutState;
  entityType: "provider" | "facility" | "business" | "user";
  locale: string;
  country: string;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CheckoutStepSuccess({ state, entityType, onClose }: CheckoutStepSuccessProps) {
  const { t } = useLingui();

  const { quantity, seatAutoAllocated, loading: seatAllocating } = state;
  const seats = quantity;

  const navigateAndClose = (path: string) => {
    // Use direct assignment to avoid race with handleClose's replaceState
    onClose();
    window.location.href = path;
  };

  return (
    <m.div
      key="success"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col items-center gap-5 px-5 py-10"
    >
      {/* Success icon */}
      <m.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", delay: 0.1, stiffness: 200 }}
        className="bg-success-soft flex h-16 w-16 items-center justify-center rounded-full"
      >
        <Check className="text-success-strong h-8 w-8" aria-hidden />
      </m.div>

      {/* Thank you + confirmation */}
      <div className="text-center">
        <h3 className="text-foreground text-xl font-bold">
          <Trans>Thank you for your purchase!</Trans>
        </h3>
        <p className="text-muted-foreground mt-2 text-sm">
          <Trans>Your subscription has been activated successfully.</Trans>
        </p>
      </div>

      {/* Variant: Provider qty=1 — auto-allocated */}
      {entityType === "provider" && quantity <= 1 && (
        <div className="w-full space-y-4">
          <div className="bg-muted/50 rounded-xl p-4 text-center">
            {seatAllocating ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="text-primary h-4 w-4 animate-spin" />
                <span className="text-muted-foreground text-sm">
                  <Trans>Allocating license to your account...</Trans>
                </span>
              </div>
            ) : (
              <p className="text-foreground text-sm">
                {seatAutoAllocated
                  ? t`Your license has been automatically allocated to your account. Complete your profile to be visible to patients.`
                  : t`Your license is active. Complete your profile to be visible to patients.`}
              </p>
            )}
          </div>
          <Button className="w-full" onClick={() => navigateAndClose("/account/provider/profile")}>
            <Trans>Complete doctor profile</Trans>
          </Button>
        </div>
      )}

      {/* Variant: Provider qty>1 or Business — manage seats */}
      {((entityType === "provider" && quantity > 1) ||
        entityType === "business" ||
        entityType === "facility") && (
        <div className="w-full space-y-4">
          <div className="bg-muted/50 rounded-xl p-4 text-center">
            <p className="text-foreground text-sm">
              <Trans>
                You have {seats} doctor licenses available. Allocate them from the management
                section.
              </Trans>
            </p>
          </div>
          <Button
            className="w-full"
            onClick={() => navigateAndClose("/account/user/billing?tab=licenses")}
          >
            <Trans>Manage licenses</Trans>
          </Button>
        </div>
      )}

      {/* Variant: User — premium activated. Only perks that work today are
          listed as unlocked (packages/feature-gating catalog status "built");
          the rest sit under a muted "Coming soon" label. */}
      {entityType === "user" && (
        <div className="w-full space-y-4">
          <div className="bg-muted/50 rounded-xl p-4">
            <p className="text-foreground mb-3 text-center text-sm font-medium">
              <Trans>Your Premium plan is active! Here's what you unlocked:</Trans>
            </p>
            <ul className="text-muted-foreground space-y-1.5 text-xs">
              <li className="flex items-start gap-2">
                <Check className="text-success-strong mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <Trans>10 active appointments + unlimited favorite doctors</Trans>
              </li>
              <li className="flex items-start gap-2">
                <Check className="text-success-strong mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <Trans>Medical vault & medical journal</Trans>
              </li>
              <li className="flex items-start gap-2">
                <Check className="text-success-strong mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <Trans>Calendar sync with Google, Apple and Outlook</Trans>
              </li>
              <li className="flex items-start gap-2">
                <Check className="text-success-strong mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <Trans>Family accounts for your loved ones</Trans>
              </li>
            </ul>
            <p className="text-muted-foreground mt-4 mb-1.5 text-[0.6875rem] font-semibold tracking-wide uppercase">
              <Trans>Coming soon</Trans>
            </p>
            <ul className="text-muted-foreground space-y-1.5 text-xs">
              <li className="flex items-start gap-2">
                <Clock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <Trans>Free teleconsultations</Trans>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <Trans>Priority availability alerts</Trans>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <Trans>Priority support</Trans>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <Trans>Health calendar</Trans>
              </li>
            </ul>
          </div>
          <Button
            className="w-full"
            onClick={() => navigateAndClose("/account/user/billing?success=1")}
          >
            <Trans>Explore your account</Trans>
          </Button>
        </div>
      )}

      {/* Later link */}
      <button
        onClick={() => navigateAndClose("/account/user/billing?success=1")}
        className="text-muted-foreground hover:text-foreground text-sm underline underline-offset-4"
      >
        <Trans>I'll do this later</Trans>
      </button>
    </m.div>
  );
}
